// Owner: Live Chat

import _ from 'lodash';
import {useEffect} from 'react';

import {usePrevious} from '../../../../../../../libs/shared/helpers/src/react/hooks/hookHelpers';
import {ReplyTypesEnum} from '../../../../../../helpers/models/recipientHelpers';
import {useAppDispatch, useAppSelector} from '../../../../../../helpers/reduxHelpers';
import {ChannelTypesEnum} from '../../../../../../models/api/channelCommonTypes';
import {ChannelModel} from '../../../../../../models/api/channelModel';
import {ConversationModel} from '../../../../../../models/api/conversationModel';
import {DraftMessageModel, MessageVisualStatusesEnum} from '../../../../../../models/api/messageModel';
import {findMessageComposer} from '../../../../../../services/composers';
import {ReplyLocationsEnum} from '../../../../../../services/tracking';
import {getMessages} from '../../../../../../store/data/dataSelectors';
import {
  deleteEmptyDraftsAction,
  isEmptyNonEmailDraft,
  replyToMessageAction,
} from '../../../../../../store/data/messages/draftMessagesActions';
import {ViewerPanelId} from '../../../../../../store/panels/panelStateBase';

// This useEffect checks for changes in the list of draft messages and creates a draft
// if a user has no active drafts, no shared drafts, and can reply to a message. This opens
// the composer by default for chat channels. This effect also clears private drafts when a shared
// draft is created.
export const useOpenComposerByDefaultEffect = (
  panelId: ViewerPanelId,
  composeDraftMessages: ReadonlyArray<DraftMessageModel>,
  conversation: ConversationModel,
  userId: number,
  isAllLoadedBottom: boolean,
  shouldOpenComposerByDefault: boolean,
  replyChannel?: ChannelModel,
) => {
  const dispatch = useAppDispatch();
  const emptyDraftMessageUids = composeDraftMessages
    .filter((draft) => isEmptyNonEmailDraft(draft))
    .map((draft) => draft.uid);

  // Need to do comparison on the UIDs as the reference to composeDraftMessages can change
  // without any new drafts added. We should exit early if there is no change.
  const previousComposeDraftMessages = usePrevious(composeDraftMessages);
  const previousComposeDraftMessageUids = previousComposeDraftMessages?.map((draft) => draft.uid);
  const previousEmptyDraftUids = usePrevious(emptyDraftMessageUids);

  const messages = useAppSelector(getMessages);

  useEffect(() => {
    const composeDraftMessageUids = composeDraftMessages.map((draft) => draft.uid);

    const hasNonEmptyAuthoredDrafts = composeDraftMessages.some(
      (draft) => draft.authorId === userId && !isEmptyNonEmailDraft(draft),
    );

    // We check here if any existing drafts became empty, so we can clear the empty drafts and create a fresh one.
    // This mostly helps us consolidate the drafts when a user has multiple authored drafts or two instances of the same user are editing shared drafts.
    const hasNewlyEmptyDrafts = composeDraftMessages.some(
      (draft) =>
        isEmptyNonEmailDraft(draft) &&
        !previousEmptyDraftUids?.includes(draft.uid) &&
        previousComposeDraftMessageUids?.includes(draft.uid) &&
        draft.id !== 0 &&
        draft.authorId === userId,
    );

    // Only clear empty drafts automatically if the user is the author of another draft with content, no need to consolidate drafts if this is the only one.
    if (hasNewlyEmptyDrafts && hasNonEmptyAuthoredDrafts) {
      dispatch(deleteEmptyDraftsAction({conversationId: conversation.id}));
      return;
    }

    const newDraftMessages = composeDraftMessages.filter(
      (draft) => !previousComposeDraftMessageUids?.includes(draft.uid),
    );

    // If the user's message failed to send for whatever reason, it will be re-inserted into the list of drafts. In this case, we should clear any composers we generated after sending
    // and show the previous draft instead.
    const hasNewDraftError = newDraftMessages.some((draft) => draft.error);

    if (hasNewDraftError) {
      dispatch(deleteEmptyDraftsAction({conversationId: conversation.id}));
      return;
    }

    const emptyAuthoredDraft = composeDraftMessages.find(
      (draft) => isEmptyNonEmailDraft(draft) && draft.authorId === userId,
    );

    // Messages being added to the drafts list with existing content indicates a draft in sending state, which should not apply the effect.
    // If a user is logged into two instances of Front client, an authored shared draft can be added with existing content, but the draft.sentAt will be more
    // recent than the empty composer create time, so we should still apply the effect.
    const hasDraftInSendingState = newDraftMessages.some(
      (draft) =>
        draft.authorId === userId &&
        (!isEmptyNonEmailDraft(draft) || draft.id !== 0) &&
        emptyAuthoredDraft &&
        emptyAuthoredDraft.createdAt > draft.sentAt,
    );

    // This effect only applies to chat channels for now
    if (
      !shouldOpenComposerByDefault ||
      !replyChannel ||
      !conversation.lastMessage ||
      replyChannel.type !== ChannelTypesEnum.FRONT_CHAT ||
      hasDraftInSendingState ||
      _.isEqual(composeDraftMessageUids, previousComposeDraftMessageUids)
    ) {
      return;
    }

    // There exists a message that can be replied to and there are no current drafts
    if (composeDraftMessages.length === 0) {
      // Find the message that we want to reply to.
      const lastMessage = messages[conversation.lastMessage.uid];

      // We cannot reply to partial messages, return early.
      if (lastMessage?.visualStatus === MessageVisualStatusesEnum.PARTIAL) {
        return;
      }

      dispatch(
        replyToMessageAction({
          panelId,
          conversationId: conversation.id,
          replyToUid: conversation.lastMessage.uid,
          replyType: ReplyTypesEnum.ALL,
          location: ReplyLocationsEnum.BOTTOM_OF_CONVERSATION,
          shouldCreateConversation: false,
          shouldPopout: false,
          overrides: {},
          shouldOpenComposerByDefault,
        }),
      );

      return;
    }

    // If user is the author of at least one draft with content, clear their empty drafts to only show drafts with content
    if (hasNonEmptyAuthoredDrafts) {
      dispatch(deleteEmptyDraftsAction({conversationId: conversation.id}));
    }

    // Only focus the composer when creating a new composer after sending a message
    // Opening a conversation or deleting a draft will not focus the composer
    const shouldFocusComposer =
      previousComposeDraftMessages && previousComposeDraftMessages.length > 0 && newDraftMessages.length > 0;
    const composer = shouldFocusComposer && findMessageComposer(newDraftMessages[0].uid);

    if (composer) {
      composer.focus();
    }
  }, [
    dispatch,
    messages,
    panelId,
    composeDraftMessages,
    previousComposeDraftMessages,
    previousComposeDraftMessageUids,
    previousEmptyDraftUids,
    conversation,
    replyChannel,
    isAllLoadedBottom,
    shouldOpenComposerByDefault,
    userId,
  ]);
};
